import { FC, Fragment, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { CustomError } from "../../../../../http/errors";
import { AdminCommonSteelVersion } from "../../../../../http/types/commonSteelVersions";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { Handle } from "../../../../../http/vertigrip/types/handles";
import { AdminInstallationType } from "../../../../../http/vertigrip/types/installationTypes";
import { useHandlesApi } from "../../../../../http/vertigrip/useHandles";
import { useInstallationTypesApi } from "../../../../../http/vertigrip/useInstallationTypes";
import { useVertigripSteelVersionsApi } from "../../../../../http/vertigrip/useSteelVersions";
import { useUpperExtensionsApi } from "../../../../../http/vertigrip/useUpperExtensions";
import { ItemFixingWithQuantity } from "../../../../../types";
import Button from "../../../../ui/Button";
import ControlledNumericWithQuantity from "../../../../ui/ControlledNumericWithQuantity";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface UpperExtensionForm {
  item: SearchAdminItem | null;
  installationType: AdminInstallationType | null;
  steelVersion: AdminCommonSteelVersion | null;
  handles: Handle[] | null;
  order: number | null;
  ladderExitDifference: number | null;
  fixings: ItemFixingWithQuantity[];
  visibility: VisibilityScopeValue;
}

interface UpperExtensionFormProps {
  installationTypeId?: string;
  steelVersionId?: string;
  upperExtensionId?: string;
  onCancel: () => void;
  onSave?: (itemId: string) => void;
}

const UpperExtensionForm: FC<UpperExtensionFormProps> = ({
  installationTypeId,
  steelVersionId,
  upperExtensionId,
  onCancel,
  onSave,
}: UpperExtensionFormProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const itemsApi = useItemsApi();
  const handlesApi = useHandlesApi();
  const installationTypesApi = useInstallationTypesApi();
  const steelVersionsApi = useVertigripSteelVersionsApi();
  const upperExtensionsApi = useUpperExtensionsApi();
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const [error, setError] = useState<CustomError>();

  const upperExtensionSwr = upperExtensionsApi.getAdminUpperExtension(aborter.current.signal);

  const installationTypesSwr = installationTypesApi.useAdminInstallationTypes({}, aborter.current.signal);
  const steelVersionsSwr = steelVersionsApi.useAdminSteelVersions({}, aborter.current.signal);

  const installationTypeSwr = installationTypesApi.getAdminInstallationType(aborter.current.signal);
  const steelVersionSwr = steelVersionsApi.getAdminSteelVersion(aborter.current.signal);

  useEffect(() => {
    upperExtensionId && upperExtensionSwr.trigger(upperExtensionId);
  }, [upperExtensionId]);

  useEffect(() => {
    installationTypeId && installationTypeSwr.trigger(installationTypeId);
  }, [installationTypeId]);

  useEffect(() => {
    steelVersionId && steelVersionSwr.trigger(steelVersionId);
  }, [steelVersionId]);

  const handlesData = handlesApi.useAdminHandles({ limit: 100 }, aborter.current.signal);

  const steelVersions: AdminCommonSteelVersion[] = (() => {
    const items = steelVersionsSwr.data?.items ?? [];

    if (!upperExtensionSwr.data) {
      return items;
    }

    return !items.find((x) => x.id === upperExtensionSwr.data?.vertigripSteelVersion.id)
      ? [...items, upperExtensionSwr.data?.vertigripSteelVersion]
      : items;
  })();

  const installationTypes: AdminInstallationType[] = (() => {
    const items = installationTypesSwr.data?.items ?? [];

    if (!upperExtensionSwr.data) {
      return items;
    }

    return !items.find((x) => x.id === upperExtensionSwr.data?.installationType.id)
      ? [...items, upperExtensionSwr.data?.installationType]
      : items;
  })();

  useEffect(() => {
    const anyError =
      upperExtensionSwr.error || installationTypeSwr.error || installationTypesSwr.error || steelVersionsSwr.error;

    if (anyError) {
      setError(anyError); // Set the error
      dispatch(showErrorNotification(i18n, anyError)); // Trigger the notification
    } else {
      setError(undefined); // Clear the error if no errors
    }
  }, [upperExtensionSwr.error, installationTypeSwr.error, installationTypesSwr.error, steelVersionsSwr.error]);

  return (
    <Form<UpperExtensionForm>
      defaultValues={{
        item: null,
        fixings: [],
        order: upperExtensionSwr.data?.order ?? 50,
        ladderExitDifference: upperExtensionSwr.data?.ladderExitDifference,
        visibility: upperExtensionSwr.data?.visibility,
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || !!error}
      secondaryButton={
        <Button
          buttonProps={{
            onClick: onCancel,
          }}
          type="secondary"
        >
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        const body = {
          steelVersionId: formData.steelVersion?.id!,
          ladderExitDifference: formData.ladderExitDifference!,
          order: formData.order!,
          handleIds: formData.handles?.map((x) => x.id) ?? [],
          installationTypeId: formData.installationType?.id!,
          fixings: formData.fixings
            .filter((x) => !!x.quantity)
            .map((x) => ({
              wallMaterialId: x.wallMaterial.id,
              quantity: x.quantity ?? 0,
            })),
          visibility: formData.visibility,
        };

        if (upperExtensionSwr.data?.id) {
          try {
            await upperExtensionsApi.updateAdminUpperExtension(upperExtensionSwr.data.id, body);
            onCancel();
            dispatch(showSuccessNotification(i18n.translation));
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        } else {
          try {
            const res = await upperExtensionsApi.insertAdminUpperExtension({
              ...body,
              itemId: formData.item?.id!,
            });
            onSave?.(res.id);
            dispatch(showSuccessNotification(i18n.translation));
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      {({ control, setValue, watch, disabled }) => {
        useEffect(() => {
          const ids = upperExtensionSwr.data?.handles?.map((x) => x.id) ?? [];
          const handles = handlesData.data?.items.filter((x) => ids.includes(x.id));
          setValue("handles", handles ?? []);
        }, [upperExtensionSwr.data, handlesData.data]);

        useEffect(() => {
          if (upperExtensionSwr.data) {
            setValue("order", upperExtensionSwr.data.order);
            setValue("visibility", upperExtensionSwr.data.visibility);
            setValue("ladderExitDifference", upperExtensionSwr.data.ladderExitDifference);
          }
        }, [upperExtensionSwr.data]);

        useEffect(() => {
          if (upperExtensionSwr.data) {
            const fixings: ItemFixingWithQuantity[] =
              installationTypesSwr.data?.items
                .find((x) => x.id === (upperExtensionSwr.data?.installationType?.id ?? installationTypeId))
                ?.wallMaterials.reduce((rv: ItemFixingWithQuantity[], wm) => {
                  const f = upperExtensionSwr.data?.fixings.find((x) => x.wallMaterialId === wm.id);
                  return [
                    ...rv,
                    {
                      wallMaterial: wm,
                      quantity: f?.quantity ?? 0,
                    },
                  ];
                }, []) ?? [];
            setValue("fixings", fixings);
          }
        }, [upperExtensionSwr.data, installationTypesSwr.data]);

        useEffect(() => {
          const installationType =
            installationTypes.find(
              (x) => x.id === (upperExtensionSwr.data?.installationType?.id ?? installationTypeId),
            ) ?? null;
          setValue("installationType", installationType);
        }, [installationTypes, upperExtensionSwr.data?.vertigripSteelVersion.id]);

        useEffect(() => {
          const steelVersion =
            steelVersions.find((x) => x.id === (upperExtensionSwr.data?.vertigripSteelVersion.id ?? steelVersionId)) ??
            null;
          setValue("steelVersion", steelVersion);
        }, [steelVersions, upperExtensionSwr.data?.vertigripSteelVersion.id]);

        const fixings =
          watch("installationType")?.wallMaterials.map((x, index) => ({
            wallMaterial: x,
            quantity: watch("fixings")[index]?.quantity ?? 0,
          })) ?? [];

        return (
          <FormFieldsContainer fullWidth>
            {!!upperExtensionSwr.data ? (
              <FormField label={i18n.translation.common.code}>
                <Input disabled value={upperExtensionSwr.data?.code} />
              </FormField>
            ) : (
              <Controller
                name="item"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <>
                      <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                        {({ labelId }) => {
                          const searchTerm = useRef("");
                          const aborter = useRef(new AbortController());

                          const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                          // Fetch initial data on mount
                          useEffect(() => {
                            loadOptions(searchTerm.current).then(setDefaultOptions);
                          }, []);

                          return (
                            <CustomAsyncSelect
                              {...field}
                              inputId={labelId}
                              defaultOptions={defaultOptions}
                              loadOptions={loadOptions}
                              isClearable
                              isDisabled={disabled}
                              getOptionLabel={(x) => x.code}
                              getOptionValue={(x) => x.id}
                              onChange={(value) => setValue("item", value ?? null)}
                            />
                          );
                        }}
                      </FormField>
                    </>
                  );
                }}
              />
            )}
            <Controller
              name="installationType"
              control={control}
              rules={{ required: !installationTypeId }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.installationTypes.singular}
                  error={fieldState.error}
                  hidden={!!installationTypeId}
                >
                  {({ labelId }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={installationTypes}
                      isLoading={installationTypesSwr.isLoading}
                      isDisabled={disabled}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                      onChange={(selectedOption) => setValue("installationType", selectedOption ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="steelVersion"
              control={control}
              rules={{ required: !steelVersionId }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.steelVersions.plural}
                  error={fieldState.error}
                  hidden={!!steelVersionId}
                >
                  {({ labelId }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={steelVersions}
                      isLoading={steelVersionsSwr.isLoading}
                      isDisabled={disabled}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                      onChange={(selectedOption) => setValue("steelVersion", selectedOption ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="handles"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.handles.plural} error={fieldState.error}>
                  {({ labelId }) => (
                    <CustomSelect<Handle, true>
                      {...field}
                      inputId={labelId}
                      isMulti
                      options={handlesData.data?.items ?? []}
                      isLoading={handlesData.isLoading}
                      isDisabled={disabled}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.code}
                      // onChange={(selectedOption) => setValue("handles", selectedOption ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="ladderExitDifference"
              control={control}
              rules={{ required: true, min: 0 }}
              defaultValue={0}
              render={({ field, fieldState }) => (
                <FormField label={`${i18n.translation.common.ladderExitDifference} [m]`} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      readOnly={disabled}
                      onBlur={(value) => setValue("ladderExitDifference", value ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      readOnly={disabled}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />

            {fixings.length > 0 && (
              <FormField label={i18n.translation.common.nrOfFixingItemsRequired}>
                <ControlledNumericWithQuantity.List>
                  {fixings.map((x) => (
                    <Fragment key={x.wallMaterial.id}>
                      <ControlledNumericWithQuantity
                        item={{
                          id: x.wallMaterial.id,
                          display: x.wallMaterial.name,
                          disabled: true,
                        }}
                        controls={{
                          scale: 1,
                        }}
                        quantity={{
                          quantity: x.quantity ?? 0,
                          onUpdate: (id, quantity) =>
                            setValue(
                              "fixings",
                              fixings.reduce((rv: ItemFixingWithQuantity[], i) => {
                                if (i.wallMaterial.id === id) {
                                  return [...rv, { ...i, quantity: quantity }];
                                }
                                return [...rv, i];
                              }, []),
                            ),
                        }}
                      />
                    </Fragment>
                  ))}
                </ControlledNumericWithQuantity.List>
              </FormField>
            )}
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default UpperExtensionForm;
